<template>
  <div class="search-page">
    <!-- Outlet Image -->
    <div class="page-title">
      <div class="outlet-image-container">
        <img
          :src="`/assets/${settings.siteName}/img/outlet.png`"
          alt="Outlet"
          class="outlet-image"
        />
      </div>
    </div>

    <!-- Show loading screen if still loading -->
    <div v-if="isLoading" class="loading-placeholder">
      <div class="shimmer-placeholder amazon-logo-placeholder"></div>
      <div class="shimmer-placeholder title-placeholder"></div>
      <div class="shimmer-placeholder text-placeholder"></div>
      <div class="shimmer-placeholder slider-placeholder"></div>
      <div class="shimmer-placeholder slider-placeholder"></div>
      <div class="shimmer-placeholder button-placeholder"></div>
    </div>

    <!-- Show actual content when loading is done -->
    <div v-else>
      <div v-if="parentOutletItem">
        <div class="parent-item-container" @click="backToParent()">
          <div class="outlet-item-action">
            <span class="material-symbols-outlined close-icon em2"
              >chevron_left</span
            >
          </div>
          <div class="outlet-item-name">{{ parentOutletItem.name }}</div>
        </div>
      </div>

      <div v-if="!searching"
        v-for="item in outletItems"
        :key="item.id"
        :class="{
          'outlet-item-in': isLoaded,
          selecting: selectingItem,
          unselecting: !selectingItem,
        }"
      >
        <div class="outlet-item-container" @click="toggleOutletItem(item)">
          <div class="outlet-item-image-container">
            <div v-if="item.imgSrc" class="outlet-item-image-container-image">
              <img
                :src="item.imgSrc"
                alt="Outlet Item"
                class="outlet-item-image"
              />
            </div>
            <span v-else class="material-symbols-outlined close-icon em2"
              >category</span
            >
          </div>
          <div class="outlet-item-name">{{ item.name }}</div>
          <div class="outlet-item-action">
            <span class="material-symbols-outlined close-icon em2"
              >chevron_right</span
            >
          </div>
        </div>
      </div>

      <div v-if="searching" class="search-results-container">
        <div v-if="loadingSearchResults">
          <div class="loading-container">
            <span class="material-symbols-outlined spinning">sync</span>
            <div class="loading-text">AI Deal Magic in Progress...</div>
            <div class="loading-subtext">Powered by MommyDeals AI</div>
          </div>
          <LoadMoreDeals />
        </div>
        <div v-else>
          <Deals :listOfDeals="dealsList" :directLinks="true" :showPrice="true" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import util from "@/helpers/util";
import settings from "@/settings";
import api from "@/api";
import { OutletItem } from "@/classes/OutletItem";
import Deals from "@/components/Deals.vue";
import LoadMoreDeals from "@/components/LoadMoreDeals.vue";
import { DealDetails } from "@/classes/DealDetails";
import { WebsiteProduct } from "@/classes/WebsiteProduct";
import { globalStore } from "@/stores/globalStore";
import { useRouter } from 'vue-router';

// Refs
const isLoading = ref(true);
const isLoaded = ref(false);
const affiliateInfo = ref<{ amazonTag: string; sharedid: string } | null>(null);
const parentOutletItem = ref<OutletItem | null>(null);
const outletItems = ref<OutletItem[]>([]);
const allOutletItems = ref<OutletItem[]>([]);
const searching = ref(false);
const loadingSearchResults = ref(false);
const dealsList = ref<DealDetails[]>([]);
const selectingItem = ref(true);
const searchResults = ref<any>(null);
const $globalStore = globalStore();
const router = useRouter();

// Computed

// Lifecycle hooks
onMounted(async () => {
  document.title =
    settings.siteNameCaps + " | " + settings.siteNameCaps + " Outlet";

  // Simulate loading time to prevent content shifting
  setTimeout(() => {
    isLoading.value = false;
  }, 1000);

  affiliateInfo.value = util.getAffiliateParams(settings.siteName);

  //GET OUTLET FROM BACKEND
  allOutletItems.value = await getOutletItems();
  
  //SELECT ROOT - analyze the url to see where we should start:
  //the url will be like this: /outlet/cat1/cat2/cat3
  //we need to find the last part of the url and use that to select the item
  const finalItem = findItemFromUrl(window.location.pathname);
  if(finalItem)
  {
    selectItem(finalItem);
    parentOutletItem.value = finalItem;
  }
  else
  {
    selectItem(null);
  }

  setTimeout(() => {
    isLoaded.value = true;
  }, 500);
});


const findItemFromUrl = (url: string) => {
  //start from the first part of the url and find the item
  const decodedUrl = decodeURIComponent(url);
  const urlParts = decodedUrl.split('/');
  if(urlParts.length === 0) return null;
  
  const partsWithoutOutlet = urlParts.filter(p => p.toLowerCase() !== 'outlet');
  let parentId = null;
  let finalItem = null;
  for(const part of partsWithoutOutlet){
    const foundItem = findItemFromName(part, parentId);
    if(foundItem){
      finalItem = foundItem;
      parentId = foundItem.id;
    }
  }
  
  return finalItem;
}

const findItemFromName = (name: string, parentId: string | null) => {

  //replace -  with spaces
  const cleanedName = name.replace(/-/g, ' ');

  return allOutletItems.value.find(i => i.name.toLowerCase() === cleanedName.toLowerCase() && i.parentId === parentId);  
}

//spaces should be replaced with -
const itemToUrl = (item: OutletItem | null) : string => {
  if(item === null) return '';
  if(item.parentId === null) return item.name.replace(/ /g, '-');
  return itemToUrl(allOutletItems.value.find(i => i.id === item.parentId)!) + '/' + item.name.replace(/ /g, '-');
}

const getOutletItems = async () => {
  const response = await api.getOutletItems();
  console.log("getOutletItems", response);
  return response.data;
};

const toggleOutletItem = async (item: OutletItem) => {
  selectingItem.value = true;
  if (item.type === "CATEGORY") {
    parentOutletItem.value = item;
    selectItem(item);
  } else if (item.type === "SEARCH") {
    parentOutletItem.value = item;
    search(item);
  } else {
    //TODO: Implement other types
    //DO NOTHING
  }

};

const search = async (item: OutletItem) => {
  searching.value = true;
  loadingSearchResults.value = true;
  searchResults.value = await api.getSearchResults(
    item.searchQuery,
    item.maxPrice,
    item.minPercentOff,
    $globalStore.categoryNodes.join(",")
  );
  loadingSearchResults.value = false;

  //FEED THE PRODUCTS INTO A DEAL DETAILS LIST
  let dealDetailsList: DealDetails[] = [];
  //console.log('searchResults.value.data.products', searchResults.value.data.products)
  searchResults.value.data.products.forEach((product: WebsiteProduct) => {
    let dealDetails = new DealDetails({
      DealID: 0,
      Title: product.title,
      Description: "",
      ImgUrl: product.imgUrl,
      RetailPrice: product.retailPrice,
      DealPrice: product.salePrice,
      DealURL: "https://www.amazon.com/dp/" + product.asin,
      DealProvider: "Amazon",
      IsActive: false,
      IsArchived: false,
      InsertDate: new Date(),
      UpdateDate: new Date(),
      ProductID: product.asin,
      Tag: "faf2",
      isExpired: false,
      PromoCode: "",
      isSoldOut: false,
      PrettyTitle: "",
      PrettyDesc: "",
    });

    //CALCULATE PERCENT OFF
    if (dealDetails.DealPrice == dealDetails.RetailPrice) {
      dealDetails.RetailPrice = null;
    }

    const discount = util.getDiscountPercentage(
      dealDetails.DealPrice,
      dealDetails.RetailPrice
    );
    dealDetails.PercentOff = discount;

    dealDetailsList.push(dealDetails);
  });

  //console.log('dealDetailsList', dealDetailsList)
  dealsList.value = dealDetailsList;
};

const backToParent = () => {
  searching.value = false;
  selectingItem.value = false;
  
  if (parentOutletItem.value.parentId === null) {
    parentOutletItem.value = null;
  }
  else{
    parentOutletItem.value = allOutletItems.value.find(i => i.id === parentOutletItem.value.parentId);
  }

  //SELECT PARENT
  selectItem(parentOutletItem.value);

  
};

const selectItem = (item: OutletItem) => {
    if(item){
        outletItems.value = allOutletItems.value.filter(
            (i) => i.parentId === item.id
        ).sort((a, b) => a.sortOrder - b.sortOrder) || [];
    }
    else{
        outletItems.value = allOutletItems.value.filter(
            (i) => i.parentId === null
        ).sort((a, b) => a.sortOrder - b.sortOrder) || [];
    }


    //UPDATE URL
    //router.push('/outlet/' + itemToUrl(item));
    window.history.pushState({}, '', '/outlet/' + itemToUrl(item));
    
};

</script>

<style scoped>
.loading-placeholder {
  margin-top: 20px;
}

.shimmer-placeholder {
  animation-duration: 1s;
  animation-fill-mode: forwards;
  animation-iteration-count: infinite;
  animation-name: placeHolderShimmer;
  animation-timing-function: linear;
  background: #f6f7f9;
  background-image: linear-gradient(
    to right,
    #f6f7f9 0%,
    #e9ebee 20%,
    #f6f7f9 40%,
    #f6f7f9 100%
  );
  background-repeat: no-repeat;
  background-size: 100%;
}
.amazon-logo-placeholder {
  width: 150px;
  height: 60px;
  margin: 0 auto;
}

.title-placeholder {
  width: 60%;
  height: 30px;
  margin: 20px auto;
}

.text-placeholder {
  width: 80%;
  height: 15px;
  margin: 10px auto;
}

.slider-placeholder {
  width: 100%;
  height: 25px;
  margin: 20px 0;
}

.button-placeholder {
  width: 200px;
  height: 40px;
  margin: 20px auto;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
  margin-bottom: 20px;
}

.loading-text {
  font-size: 1.5em;
  font-weight: bold;
}

.loading-subtext {
  font-size: 0.8em;
  color: #666;
  margin-top: 5px;
}


/* Shimmer animation */
@keyframes placeHolderShimmer {
  0% {
    background-position: -468px 0;
  }

  100% {
    background-position: 468px 0;
  }
}

.page-title {
  background-color: #ff5e8a;
  padding: 5px;
}

.outlet-image-container {
  height: auto;
  display: flex;
  justify-content: center;
  align-items: center;
}

.outlet-image-container img {
  overflow: auto;
  overflow-clip-margin: unset;
}

.outlet-item-container {
  display: grid;
  grid-template-columns: 60px 1fr 60px;
  align-items: center;
  gap: 5px;
  border-bottom: 1px solid #e0e0e0;
  cursor: pointer;
}

.outlet-item-image-container img {
  width: 60px; /* Ensure it's square */
  height: 60px;
  object-fit: cover; /* Keeps the image filled without distortion */
  border-radius: 50%; /* Makes it a circle */
}
.outlet-item-image-container span {
  font-size: 60px;
  color: #ff5e8a;
  max-height: 60px;
}
.outlet-item-name {
  font-size: 0.85em;
  font-weight: bold;
}

/* Animate new item sliding in from the right */
.outlet-item-in.selecting {
  animation: flyInRight 0.25s ease-in-out forwards;
}

.outlet-item-in.unselecting {
  animation: flyInLeft 0.25s ease-in-out forwards;
}

@keyframes flyInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0%);
    opacity: 1;
  }
}

@keyframes flyInLeft {
  from {
    transform: translateX(-100%);
    opacity: 0;
  }
  to {
    transform: translateX(0%);
    opacity: 1;
  }
}

.parent-item-container {
  display: grid;
  grid-template-columns: 60px 1fr;
  align-items: center;
  border-bottom: 1px solid #e0e0e0;
  cursor: pointer;
}

.parent-item-container .outlet-item-action {
  width: 60px;
  height: 55px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.parent-item-container .outlet-item-name {
  justify-self: start;
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* larger sizes */
@media (min-width: 768px) {
  .outlet-item-container {
    grid-template-columns: 160px 1fr 60px;
    gap: 15px;
  }
  .outlet-item-image-container img {
    width: 160px;
    height: 160px;
  }
  .outlet-item-name {
    font-size: 1.85em;
    text-align: left;
  }

  .outlet-item-image-container span{
    font-size: 160px;
  }
}

.search-outlet-input {
  width: 100%;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 5px;
}

.search-outlet-input:focus {
  outline: none;
  border-color: #ff5e8a;
}

</style>


